/* eslint-disable no-nested-ternary */
import GenericLoading from '@cannect/components/GenericLoading'
import { Button } from '@cannect/new-components/atoms'
import Input from 'new-components/Input'
import Select from 'new-components/Select'
import useRegistration, {
  DocumentUploadData,
  RegistrationPath
} from 'pages/RegistrationAssociation/hooks/useRegistration'
import React, { useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from 'services/api'
import { dateMask, formatCep, formatCpf, formatPhone } from 'utils/formatters'
import * as SharedStyles from '../../../styles'
import { getFieldError } from '../../helpers'
import useRegistrationAssociationForm from '../../hooks/useRegistrationAssociationForm'
import { GENDER_IDENTITY_LIST, GENDER_LIST, NATIONALITY_LIST, UF_LIST } from './constants'
import * as Styles from './styles'

interface ViaCepResponse {
  cep: string
  logradouro: string
  complemento: string
  bairro: string
  localidade: string
  uf: string
  erro?: boolean
}

// Ícones SVG inline
const UploadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
)

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const LoadingIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
    <line x1="2" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="22" y2="12" />
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
  </svg>
)

// Interface para resposta da API de upload
interface UploadDocumentResponse {
  success: boolean
  url: string
}

// Função para fazer upload do documento para o S3
async function uploadDocumentToS3(file: File): Promise<string | null> {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post<UploadDocumentResponse>('/document/upload-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    if (response.data.success && response.data.url) {
      return response.data.url
    }
    return null
  } catch (error) {
    console.error('Erro ao fazer upload do documento:', error)
    return null
  }
}

// Componente de Upload Compacto com upload imediato
interface CompactFileUploadProps {
  label: string
  files: File[]
  documentType: string
  onUpload: (files: File[]) => void
  onDocumentUploaded: (document: DocumentUploadData) => void
  onDocumentRemoved: (type: string) => void
  accept?: string
}

function CompactFileUpload({
  label,
  files,
  documentType,
  onUpload,
  onDocumentUploaded,
  onDocumentRemoved,
  accept = '.pdf,.jpg,.jpeg,.png'
}: CompactFileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const hasFile = files.length > 0

  const handleClick = () => {
    if (!isUploading) {
      inputRef.current?.click()
    }
  }

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files
    if (selectedFiles && selectedFiles.length > 0) {
      const file = selectedFiles[0]
      onUpload([file])

      // Inicia o upload imediatamente
      setIsUploading(true)
      setUploadSuccess(false)

      const url = await uploadDocumentToS3(file)

      if (url) {
        const documentData: DocumentUploadData = {
          type: documentType,
          name_file: file.name,
          extension: file.type,
          url
        }
        onDocumentUploaded(documentData)
        setUploadSuccess(true)
        toast.success(`${label} enviado com sucesso!`)
      } else {
        toast.error(`Erro ao enviar ${label}. Tente novamente.`)
        onUpload([])
        if (inputRef.current) {
          inputRef.current.value = ''
        }
      }

      setIsUploading(false)
    }
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    onUpload([])
    onDocumentRemoved(documentType)
    setUploadSuccess(false)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const getIcon = () => {
    if (isUploading) return <LoadingIcon />
    if (hasFile && uploadSuccess) return <CheckIcon />
    return <UploadIcon />
  }

  const getHint = () => {
    if (isUploading) return 'Enviando...'
    return 'Clique para enviar'
  }

  return (
    <Styles.CompactUpload hasFile={hasFile && uploadSuccess} onClick={handleClick}>
      <input ref={inputRef} type="file" accept={accept} onChange={handleChange} disabled={isUploading} />
      <Styles.UploadIconContainer hasFile={hasFile && uploadSuccess} isLoading={isUploading}>
        {getIcon()}
      </Styles.UploadIconContainer>
      <Styles.UploadContent>
        <Styles.UploadLabel>{label}</Styles.UploadLabel>
        {hasFile ? (
          <Styles.UploadFileName title={files[0].name}>
            {isUploading ? 'Enviando...' : files[0].name}
          </Styles.UploadFileName>
        ) : (
          <Styles.UploadHint>{getHint()}</Styles.UploadHint>
        )}
      </Styles.UploadContent>
      {hasFile && !isUploading && (
        <Styles.RemoveFileButton onClick={handleRemove} type="button">
          <CloseIcon />
        </Styles.RemoveFileButton>
      )}
    </Styles.CompactUpload>
  )
}

interface RegistrationFormProps {
  onGoBack: () => void
  path: RegistrationPath
}

export default function RegistrationForm({ onGoBack, path: _path }: RegistrationFormProps) {
  const history = useHistory()
  const goBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    history.push('/AreaAssociados')
  }
  const registrationFields = useRegistration()
  const {
    loading,
    name,
    nationality,
    genderIdentity,
    gender,
    cpf,
    rg,
    issuingAuthorityRg,
    issuingAuthorityUfRg,
    birthdate,
    cellphone,
    email,
    emailConfirmation,
    password,
    passwordConfirmation,
    addressCep,
    address,
    addressNumber,
    addressComplement,
    addressDistrict,
    addressCity,
    addressState,
    healthPlan,
    identityDocument,
    setIdentityDocument,
    prescription,
    setPrescription,
    medicalReport,
    setMedicalReport,
    addUploadedDocument,
    removeUploadedDocument,
    changeFieldValue,
    errors
  } = registrationFields

  const [loadingCep, setLoadingCep] = useState(false)

  const { onRegistrationFormSubmit } = useRegistrationAssociationForm(registrationFields)

  const fetchAddressByCep = async (cep: string) => {
    // Remove caracteres não numéricos
    const cleanCep = cep.replace(/\D/g, '')

    if (cleanCep.length !== 8) return

    setLoadingCep(true)
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`)
      const data: ViaCepResponse = await response.json()

      if (data.erro) {
        toast.error('CEP não encontrado')
        return
      }

      // Preenche os campos de endereço
      if (data.logradouro) changeFieldValue('address', data.logradouro)
      if (data.bairro) changeFieldValue('addressDistrict', data.bairro)
      if (data.localidade) changeFieldValue('addressCity', data.localidade)
      if (data.uf) changeFieldValue('addressState', data.uf)
    } catch (err) {
      console.error('Error fetching address by CEP:', err)
      toast.error('Erro ao buscar endereço pelo CEP')
    } finally {
      setLoadingCep(false)
    }
  }

  const handleCepChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedCep = formatCep(event.target.value)
    changeFieldValue('addressCep', formattedCep)

    // Se o CEP tiver 8 dígitos, busca o endereço
    const cleanCep = formattedCep.replace(/\D/g, '')
    if (cleanCep.length === 8) {
      fetchAddressByCep(cleanCep)
    }
  }

  return (
    <Styles.RegistrationFormContainer onSubmit={onRegistrationFormSubmit}>
      <GenericLoading loading={loading} />

      {/* Nome e CPF */}
      <SharedStyles.InputsContainer>
        <Styles.VariableWidthContainer width="67%">
          <Input
            id="name"
            label="Nome completo*"
            placeholder="Digite seu nome completo"
            value={name}
            errorMessage={getFieldError('name', errors)}
            onChange={(event) => changeFieldValue('name', event.target.value)}
          />
        </Styles.VariableWidthContainer>
        <Styles.VariableWidthContainer width="33%">
          <Input
            id="cpf"
            label="CPF*"
            placeholder="Digite seu CPF"
            value={cpf}
            errorMessage={getFieldError('cpf', errors)}
            onChange={(event) => changeFieldValue('cpf', formatCpf(event.target.value))}
          />
        </Styles.VariableWidthContainer>
      </SharedStyles.InputsContainer>

      {/* Identidade de gênero, Gênero e Nacionalidade */}
      <SharedStyles.InputsContainer>
        <Select
          id="gender-identity"
          label="Identidade de gênero*"
          options={GENDER_IDENTITY_LIST}
          value={genderIdentity}
          errorMessage={getFieldError('genderIdentity', errors)}
          onChange={(newValue) => changeFieldValue('genderIdentity', newValue)}
        />
        <Select
          id="gender"
          label="Gênero*"
          options={GENDER_LIST}
          value={gender}
          errorMessage={getFieldError('gender', errors)}
          onChange={(newValue) => changeFieldValue('gender', newValue)}
        />
        <Select
          id="nationality"
          label="Nacionalidade*"
          options={NATIONALITY_LIST}
          value={nationality}
          errorMessage={getFieldError('nationality', errors)}
          onChange={(newValue) => changeFieldValue('nationality', newValue)}
        />
      </SharedStyles.InputsContainer>

      {/* RG, Órgão Emissor e UF */}
      <SharedStyles.InputsContainer>
        <Input
          id="rg"
          label="RG*"
          placeholder="Digite seu RG"
          value={rg}
          errorMessage={getFieldError('rg', errors)}
          onChange={(event) => changeFieldValue('rg', event.target.value)}
        />
        <Input
          id="issuing-authority-rg"
          label="Órgão Emissor*"
          placeholder="Ex: SSP"
          value={issuingAuthorityRg}
          errorMessage={getFieldError('issuingAuthorityRg', errors)}
          onChange={(event) => changeFieldValue('issuingAuthorityRg', event.target.value)}
        />
        <Select
          id="issuing-authority-uf-rg"
          label="UF Emissor*"
          options={UF_LIST}
          value={issuingAuthorityUfRg}
          errorMessage={getFieldError('issuingAuthorityUfRg', errors)}
          onChange={(newValue) => changeFieldValue('issuingAuthorityUfRg', newValue)}
        />
      </SharedStyles.InputsContainer>

      {/* Data de nascimento e Celular/WhatsApp */}
      <SharedStyles.InputsContainer>
        <Input
          id="birthdate"
          label="Data de nascimento*"
          placeholder="dd/mm/aaaa"
          value={birthdate}
          errorMessage={getFieldError('birthdate', errors)}
          onChange={(event) => changeFieldValue('birthdate', dateMask(event.target.value))}
        />
        <Input
          id="cellphone"
          label="Celular/WhatsApp*"
          placeholder="(99) 99999-9999"
          value={cellphone}
          errorMessage={getFieldError('cellphone', errors)}
          onChange={(event) => changeFieldValue('cellphone', formatPhone(event.target.value))}
        />
        <Input
          id="health-plan"
          label="Plano de Saúde"
          placeholder="Digite o nome do seu plano de saúde (opcional)"
          value={healthPlan}
          onChange={(event) => changeFieldValue('healthPlan', event.target.value)}
        />
      </SharedStyles.InputsContainer>

      {/* CEP e Endereço */}
      <SharedStyles.InputsContainer>
        <Styles.VariableWidthContainer width="30%">
          <Styles.CepInputContainer>
            <Input
              id="address-cep"
              label="CEP*"
              placeholder="00000-000"
              value={addressCep}
              errorMessage={getFieldError('addressCep', errors)}
              onChange={handleCepChange}
              maxLength={9}
            />
            {loadingCep && <Styles.CepLoadingIndicator>Buscando...</Styles.CepLoadingIndicator>}
          </Styles.CepInputContainer>
        </Styles.VariableWidthContainer>
        <Styles.VariableWidthContainer width="70%">
          <Input
            id="address"
            label="Endereço*"
            placeholder="Nome da rua"
            value={address}
            errorMessage={getFieldError('address', errors)}
            onChange={(event) => changeFieldValue('address', event.target.value)}
          />
        </Styles.VariableWidthContainer>
      </SharedStyles.InputsContainer>

      {/* Número e Complemento */}
      <SharedStyles.InputsContainer>
        <Styles.VariableWidthContainer width="30%">
          <Input
            id="address-number"
            label="Número*"
            placeholder="123"
            value={addressNumber}
            errorMessage={getFieldError('addressNumber', errors)}
            onChange={(event) => changeFieldValue('addressNumber', event.target.value)}
          />
        </Styles.VariableWidthContainer>
        <Styles.VariableWidthContainer width="70%">
          <Input
            id="address-complement"
            label="Complemento"
            placeholder="Apto, Bloco, etc"
            value={addressComplement}
            errorMessage={getFieldError('addressComplement', errors)}
            maxLength={64}
            onChange={(event) => changeFieldValue('addressComplement', event.target.value)}
          />
        </Styles.VariableWidthContainer>
      </SharedStyles.InputsContainer>

      {/* Bairro e Cidade */}
      <SharedStyles.InputsContainer>
        <Input
          id="address-district"
          label="Bairro*"
          placeholder="Nome do bairro"
          value={addressDistrict}
          errorMessage={getFieldError('addressDistrict', errors)}
          onChange={(event) => changeFieldValue('addressDistrict', event.target.value)}
        />
        <Input
          id="address-city"
          label="Cidade*"
          placeholder="Nome da cidade"
          value={addressCity}
          errorMessage={getFieldError('addressCity', errors)}
          onChange={(event) => changeFieldValue('addressCity', event.target.value)}
        />
        <Select
          id="address-state"
          label="Estado*"
          options={UF_LIST}
          value={addressState}
          errorMessage={getFieldError('addressState', errors)}
          onChange={(newValue) => changeFieldValue('addressState', newValue)}
        />
      </SharedStyles.InputsContainer>

      {/* E-mail e Confirmação */}
      <SharedStyles.InputsContainer>
        <Styles.VariableWidthContainer width="50%">
          <Input
            id="email"
            label="E-mail*"
            placeholder="Digite seu e-mail"
            type="email"
            value={email}
            errorMessage={getFieldError('email', errors)}
            onChange={(event) => changeFieldValue('email', event.target.value)}
          />
        </Styles.VariableWidthContainer>
        <Styles.VariableWidthContainer width="50%">
          <Input
            id="email-confirmation"
            label="Confirmação do e-mail*"
            placeholder="Confirme seu e-mail"
            type="email"
            value={emailConfirmation}
            errorMessage={getFieldError('emailConfirmation', errors)}
            onChange={(event) => changeFieldValue('emailConfirmation', event.target.value)}
          />
        </Styles.VariableWidthContainer>
      </SharedStyles.InputsContainer>

      {/* Senha e Confirmação */}
      <SharedStyles.InputsContainer>
        <Styles.VariableWidthContainer width="50%">
          <Input
            id="password"
            label="Senha*"
            placeholder="Digite sua senha"
            type="password"
            value={password}
            errorMessage={getFieldError('password', errors)}
            onChange={(event) => changeFieldValue('password', event.target.value)}
          />
          <Styles.PasswordGuidelines>
            A senha deve ter 8 caracteres com pelo menos 1 letra maiúscula, 1 letra minúscula, 1 número e 1 caractere
            especial
          </Styles.PasswordGuidelines>
        </Styles.VariableWidthContainer>
        <Styles.VariableWidthContainer width="50%">
          <Input
            id="password-confirmation"
            label="Confirmação da senha*"
            placeholder="Confirme sua senha"
            type="password"
            value={passwordConfirmation}
            errorMessage={getFieldError('passwordConfirmation', errors)}
            onChange={(event) => changeFieldValue('passwordConfirmation', event.target.value)}
          />
          <Styles.PasswordGuidelines>
            A senha deve ter 8 caracteres com pelo menos 1 letra maiúscula, 1 letra minúscula, 1 número e 1 caractere
            especial
          </Styles.PasswordGuidelines>
        </Styles.VariableWidthContainer>
      </SharedStyles.InputsContainer>

      {/* Plano de Saúde */}
      <SharedStyles.InputsContainer />

      {/* Documentos */}
      <SharedStyles.InputsContainer>
        <Styles.DocumentSection>
          <Styles.DocumentTitle>Documentos (Opcionais)</Styles.DocumentTitle>
          <Styles.DocumentDescription>
            Envie seus documentos agora ou posteriormente na área de associados
          </Styles.DocumentDescription>
        </Styles.DocumentSection>
      </SharedStyles.InputsContainer>

      {/* Grid de Uploads */}
      <Styles.DocumentsGrid>
        <CompactFileUpload
          label="Documento Pessoal (RG ou CNH)"
          files={identityDocument}
          documentType="IDENTIDADE"
          onUpload={setIdentityDocument}
          onDocumentUploaded={addUploadedDocument}
          onDocumentRemoved={removeUploadedDocument}
        />
        <CompactFileUpload
          label="Receita Médica"
          files={prescription}
          documentType="PRESCRICAO"
          onUpload={setPrescription}
          onDocumentUploaded={addUploadedDocument}
          onDocumentRemoved={removeUploadedDocument}
        />
        <CompactFileUpload
          label="Laudo Médico"
          files={medicalReport}
          documentType="LAUDO_MEDICO"
          onUpload={setMedicalReport}
          onDocumentUploaded={addUploadedDocument}
          onDocumentRemoved={removeUploadedDocument}
        />
      </Styles.DocumentsGrid>

      {/* Botões */}
      <SharedStyles.ConfirmRegistrationContainer>
        <Button
          style={{
            textDecoration: 'underline',
            color: '#00607f',
            backgroundColor: 'transparent',
            borderColor: '#00607f'
          }}
          onClick={goBack}
          color="#00607f"
          type="button"
          variant="outline">
          Voltar
        </Button>
        <Button type="submit" style={{ backgroundColor: '#00607f', color: '#FFFFFF' }}>
          Fazer inscrição
        </Button>
      </SharedStyles.ConfirmRegistrationContainer>
    </Styles.RegistrationFormContainer>
  )
}
